import React from 'react';
import { ChecklistTable, ChoiceRow, Field, MultiChoiceRow, RowsTable, SECTION_TITLE } from './fields';
import { Input } from '../ui/input';

const RM_COLUMNS = [
  { key: 'rm_spec', label: 'RM spec / grade', width: '18%' },
  { key: 'wire_dia', label: 'Wire dia / size', width: '12%' },
  { key: 'qty_kg', label: 'Qty reqd (kg)', width: '11%' },
  { key: 'in_stock', label: 'In stock? (Y/N, kg)', width: '13%' },
  { key: 'po_or_expected', label: 'If no — PO / expected date', width: '16%' },
  { key: 'processing_by', label: 'RM processing (anneal / draw / phos.) by', width: '18%' },
  { key: 'processing_date', label: 'Processing date', width: '12%', type: 'date' },
];

const TOOL_COLUMNS = [
  { key: 'description', label: 'Tool / die', width: '24%', readOnly: true },
  { key: 'tool_no', label: 'Tool no.', width: '14%' },
  { key: 'available', label: 'Available? (Y/N)', width: '12%' },
  { key: 'new_required', label: 'New tool reqd? (Y/N)', width: '13%' },
  { key: 'ready_by', label: 'Tool ready by', width: '13%', type: 'date' },
  { key: 'remarks', label: 'Remarks (rolling dies, source)', width: '24%' },
];

const PACK_COLUMNS = [
  { key: 'material', label: 'Packaging material', width: '24%', readOnly: true },
  { key: 'spec', label: 'Spec / size', width: '16%' },
  { key: 'qty', label: 'Qty reqd', width: '10%' },
  { key: 'in_stock', label: 'In stock? (Y/N)', width: '11%' },
  { key: 'expected_date', label: 'If no — expected date', width: '14%', type: 'date' },
  { key: 'remarks', label: 'Remarks / vendor', width: '25%' },
];

const WHERE_OPTIONS = [
  { value: 'in_house', label: 'In-house' },
  { value: 'outsourced', label: 'Outsourced' },
  { value: 'na', label: 'N/A' },
];

const Block = ({ title, hint, children }) => (
  <div className="space-y-3 border-t pt-4 first:border-t-0 first:pt-0">
    <div>
      <h3 className={SECTION_TITLE}>{title}</h3>
      {hint && <p className="text-xs text-slate-500">{hint}</p>}
    </div>
    {children}
  </div>
);

export const PartReviewSection = ({ part, index, onChange }) => {
  const patch = (changes) => onChange({ ...part, ...changes });
  const setIdentification = (key, value) => patch({ identification: { ...part.identification, [key]: value } });
  const setPacking = (key, value) => patch({ packing: { ...part.packing, [key]: value } });
  const setRows = (key, rowIndex, changes) => patch({
    [key]: part[key].map((row, idx) => (idx === rowIndex ? { ...row, ...changes } : row)),
  });

  const identification = part.identification || {};
  const packing = part.packing || {};

  return (
    <div className="space-y-5" data-testid={`part-review-section-${index + 1}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2">
        <p className="text-xs text-slate-500">
          Part sheet no. <span className="font-mono font-semibold text-slate-900">{part.part_sheet_no}</span>
        </p>
        <ChoiceRow
          label="Part feasible?"
          options={[{ value: 'yes', label: 'Yes' }, { value: 'conditions', label: 'With conditions' }, { value: 'no', label: 'No' }]}
          value={part.feasible}
          onChange={(value) => patch({ feasible: value })}
          testId={`part-${index + 1}-feasible`}
        />
      </div>

      <Block title="A · Part identification">
        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-4">
          <Field label="Customer part no." value={identification.customer_part_no} onChange={(v) => setIdentification('customer_part_no', v)} testId={`part-${index + 1}-customer-part-no`} />
          <Field label="SIL part / item code" value={identification.sil_part_code} onChange={(v) => setIdentification('sil_part_code', v)} testId={`part-${index + 1}-sil-code`} />
          <Field label="Description" value={identification.description} onChange={(v) => setIdentification('description', v)} testId={`part-${index + 1}-description`} />
          <Field label="Material / grade / class" value={identification.material_grade} onChange={(v) => setIdentification('material_grade', v)} testId={`part-${index + 1}-material`} />
          <Field label="Size / thread" value={identification.size_thread} onChange={(v) => setIdentification('size_thread', v)} testId={`part-${index + 1}-size`} />
          <Field label="Drawing no. / rev." value={identification.drawing_rev} onChange={(v) => setIdentification('drawing_rev', v)} testId={`part-${index + 1}-drawing`} />
          <Field label="Surface finish / coating" value={identification.surface_finish} onChange={(v) => setIdentification('surface_finish', v)} testId={`part-${index + 1}-finish`} />
          <Field label="Customer delivery date" type="date" value={(identification.delivery_date || '').slice(0, 10)} onChange={(v) => setIdentification('delivery_date', v)} testId={`part-${index + 1}-delivery-date`} />
          <Field label="Order qty" value={identification.order_qty} onChange={(v) => setIdentification('order_qty', v)} testId={`part-${index + 1}-order-qty`} />
          <Field label="Qty in finished stock" value={identification.qty_in_stock} onChange={(v) => setIdentification('qty_in_stock', v)} testId={`part-${index + 1}-qty-stock`} />
          <Field label="Qty to produce" value={identification.qty_to_produce} onChange={(v) => setIdentification('qty_to_produce', v)} testId={`part-${index + 1}-qty-produce`} />
          <Field label="PPAP / sample level" value={identification.ppap_level} onChange={(v) => setIdentification('ppap_level', v)} placeholder="e.g. Level 3" testId={`part-${index + 1}-ppap-level`} />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <ChoiceRow label="Part status" options={[{ value: 'new', label: 'New part' }, { value: 'repeat', label: 'Repeat' }, { value: 'repeat_eng_change', label: 'Repeat with eng. change' }]}
            value={identification.part_status} onChange={(v) => setIdentification('part_status', v)} testId={`part-${index + 1}-status`} />
          <ChoiceRow label="PPAP / sample for this part" options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]}
            value={identification.ppap_required} onChange={(v) => setIdentification('ppap_required', v)} testId={`part-${index + 1}-ppap`} />
        </div>
      </Block>

      <Block title="B · Raw material" hint="Stores / Purchase — if not in stock, enter expected receipt date">
        <RowsTable columns={RM_COLUMNS} rows={part.raw_material || []} testIdPrefix={`part-${index + 1}-rm`}
          onChange={(rowIndex, changes) => setRows('raw_material', rowIndex, changes)}
          onAdd={() => patch({ raw_material: [...(part.raw_material || []), { id: `rm-${Date.now()}`, rm_spec: '', wire_dia: '', qty_kg: '', in_stock: '', po_or_expected: '', processing_by: '', processing_date: '' }] })}
          onRemove={(rowIndex) => patch({ raw_material: part.raw_material.filter((_, idx) => idx !== rowIndex) })} />
      </Block>

      <Block title="C · Tooling" hint="Tool Room">
        <RowsTable columns={TOOL_COLUMNS} rows={part.tooling || []} testIdPrefix={`part-${index + 1}-tool`}
          onChange={(rowIndex, changes) => setRows('tooling', rowIndex, changes)} />
      </Block>

      <Block title="D · Process routing — H&T, surface finish & other operations" hint="Production / PPC">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px] text-sm">
            <thead>
              <tr className="border-b text-left text-[11px] uppercase tracking-wide text-slate-500">
                <th className="w-[24%] py-2">Process step</th>
                <th className="w-[24%] py-2">Where / how</th>
                <th className="w-[20%] py-2">Vendor / machine</th>
                <th className="w-[14%] py-2">Planned date</th>
                <th className="py-2">Remarks</th>
              </tr>
            </thead>
            <tbody>
              {(part.routing || []).map((row, rowIndex) => (
                <tr key={row.id} className="border-b last:border-0" data-testid={`part-${index + 1}-routing-row-${rowIndex + 1}`}>
                  <td className="py-2 pr-2 text-slate-700">{row.step}</td>
                  <td className="py-2 pr-2">
                    <div className="flex gap-1">
                      {WHERE_OPTIONS.map((option) => (
                        <button key={option.value} type="button" aria-pressed={row.where === option.value}
                          onClick={() => setRows('routing', rowIndex, { where: row.where === option.value ? '' : option.value })}
                          className={`rounded-md border px-2 py-1 text-[11px] transition-colors duration-150 ${
                            row.where === option.value ? 'border-blue-600 bg-blue-600 text-white' : 'border-slate-200 bg-white text-slate-600 hover:border-blue-300'}`}
                          data-testid={`part-${index + 1}-routing-${rowIndex + 1}-${option.value}`}>
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </td>
                  <td className="py-2 pr-2">
                    <Input value={row.vendor_machine ?? ''} onChange={(event) => setRows('routing', rowIndex, { vendor_machine: event.target.value })}
                      className="h-8 text-sm" data-testid={`part-${index + 1}-routing-${rowIndex + 1}-vendor`} />
                  </td>
                  <td className="py-2 pr-2">
                    <Input type="date" value={row.planned_date ?? ''} onChange={(event) => setRows('routing', rowIndex, { planned_date: event.target.value })}
                      className="h-8 text-sm" data-testid={`part-${index + 1}-routing-${rowIndex + 1}-date`} />
                  </td>
                  <td className="py-2">
                    <Input value={row.remarks ?? ''} onChange={(event) => setRows('routing', rowIndex, { remarks: event.target.value })}
                      className="h-8 text-sm" data-testid={`part-${index + 1}-routing-${rowIndex + 1}-remarks`} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Block>

      <Block title="E · Packing specification & packaging material" hint="Marketing to confirm spec; Stores to confirm availability">
        <div className="grid gap-3 sm:grid-cols-2">
          <ChoiceRow label="Packing specification" options={[{ value: 'customer', label: 'Customer spec received' }, { value: 'sil_standard', label: 'SIL standard' }, { value: 'not_received', label: 'Not received' }, { value: 'to_clarify', label: 'To be clarified' }]}
            value={packing.spec_choice} onChange={(v) => setPacking('spec_choice', v)} testId={`part-${index + 1}-packing-spec`} />
          <Field label="Spec reference" value={packing.spec_ref} onChange={(v) => setPacking('spec_ref', v)} testId={`part-${index + 1}-packing-ref`} />
        </div>
        <div className="grid gap-3 sm:grid-cols-4">
          <Field label="Qty / weight per inner pack" value={packing.qty_per_inner} onChange={(v) => setPacking('qty_per_inner', v)} testId={`part-${index + 1}-inner-pack`} />
          <Field label="Qty / weight per carton" value={packing.qty_per_carton} onChange={(v) => setPacking('qty_per_carton', v)} testId={`part-${index + 1}-carton`} />
          <ChoiceRow label="Pallet type" options={[{ value: 'wood', label: 'Wood (ISPM-15)' }, { value: 'plastic', label: 'Plastic' }]}
            value={packing.pallet_type} onChange={(v) => setPacking('pallet_type', v)} testId={`part-${index + 1}-pallet`} />
          <Field label="Pallet size" value={packing.pallet_size} onChange={(v) => setPacking('pallet_size', v)} testId={`part-${index + 1}-pallet-size`} />
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <MultiChoiceRow label="Labelling / barcode / tags" options={['Customer label', 'SIL label', 'Barcode / EDI', 'Country of origin']}
            values={packing.labelling} onChange={(v) => setPacking('labelling', v)} testId={`part-${index + 1}-labelling`} />
          <MultiChoiceRow label="Protection" options={['VCI / oil', 'Poly bag', 'Desiccant', 'None']}
            values={packing.protection} onChange={(v) => setPacking('protection', v)} testId={`part-${index + 1}-protection`} />
          <MultiChoiceRow label="Special / regulatory" options={['PPWR', 'Recyclable only', 'Returnable packaging']}
            values={packing.special} onChange={(v) => setPacking('special', v)} testId={`part-${index + 1}-special`} />
        </div>
        <RowsTable columns={PACK_COLUMNS} rows={packing.materials || []} testIdPrefix={`part-${index + 1}-packmat`}
          onChange={(rowIndex, changes) => setPacking('materials', packing.materials.map((row, idx) => (idx === rowIndex ? { ...row, ...changes } : row)))} />
      </Block>

      <Block title="F · Technical feasibility" hint='Quality / Production — any "No" requires a remark'>
        <ChecklistTable items={part.technical_review || []} testIdPrefix={`part-${index + 1}-tech`}
          onChange={(rowIndex, changes) => setRows('technical_review', rowIndex, changes)} />
      </Block>

      <Block title="G · Part schedule & result">
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Planned production start" type="date" value={part.schedule?.production_start} onChange={(v) => patch({ schedule: { ...part.schedule, production_start: v } })} testId={`part-${index + 1}-prod-start`} />
          <Field label="Planned completion" type="date" value={part.schedule?.completion} onChange={(v) => patch({ schedule: { ...part.schedule, completion: v } })} testId={`part-${index + 1}-completion`} />
          <Field label="Planned dispatch date" type="date" value={part.schedule?.dispatch} onChange={(v) => patch({ schedule: { ...part.schedule, dispatch: v } })} testId={`part-${index + 1}-dispatch`} />
        </div>
        <Field label="Part-specific conditions / deviations / remarks" value={part.conditions_remarks}
          onChange={(v) => patch({ conditions_remarks: v })} testId={`part-${index + 1}-conditions`} />
      </Block>
    </div>
  );
};

export default PartReviewSection;
